// Garage system (P0) — SPEC_TECH_TREE_PIVOT.md §3/§5. Pre-incorporation: no
// company, no payroll, no cap table. Founder-months are the only labor,
// allocated across research / demo / discovery / consulting / networking /
// recruiting; pooled savings burn monthly; the PITCH prices a seed term sheet
// off the researched thesis, and ACCEPTING it is the founding transaction.
//
// P0 simplifications vs. the spec (documented in ARCHITECTURE_P0.md): savings
// are pooled (all founders day-job together at zero), garage research has a
// single focus node, one term sheet per pitch (no competing-sheet leverage),
// and story beats are deferred to P1 with the UI.

import type { GarageActivity, TermSheet } from "../types.js";
import { DISCIPLINES, GARAGE_ACTIVITIES } from "../types.js";
import type { TickContext } from "../ctx.js";
import { charge, emit } from "../ctx.js";
import { interpolate } from "../calendar.js";
import { clamp } from "../prng.js";
import { activeTree, getTechNode, gradeMult, isResearched, prereqsMet, treeAccrue } from "./tree.js";
import { closeRound } from "./finance.js";
import { processHistory } from "./history.js";
import { totalHeadcount } from "./people.js";

/** Founder-months available this month: full-time on savings, evenings-and-
 *  weekends (dayJobEff) once the pooled runway is gone. */
export function garageFounderMonths(ctx: TickContext): number {
  const g = ctx.s.garage!;
  const eff = g.savings > 0 ? 1 : ctx.content.economy.garage.dayJobEff;
  return g.founders.length * eff * ctx.monthsPerTick;
}

/** Pitch appeal, split by source: the THESIS carries the story; every other
 *  researched node with a pitchAppeal curve is tech credibility that only
 *  counts once a thesis is held — partners fund stories, and features merely
 *  corroborate one. The UI renders both parts, so keep them separate here. */
export function pitchAppealParts(ctx: TickContext): { thesis: number; tech: number; thesisHeld: boolean } {
  const tree = activeTree(ctx);
  const m = ctx.s.monthIndex;
  let thesis = 0;
  let tech = 0;
  let thesisHeld = false;
  if (tree) {
    for (const [id, r] of Object.entries(ctx.s.tree.researched).sort(([a], [b]) => a.localeCompare(b))) {
      const node = tree.nodes.find((n) => n.id === id);
      if (!node) continue;
      if (node.exclusiveGroup === "thesis") thesisHeld = true;
      if (!node.pitchAppeal) continue;
      const v = interpolate(node.pitchAppeal, m) * gradeMult(ctx, r.grade);
      if (node.exclusiveGroup === "thesis") thesis += v;
      else tech += v;
    }
  }
  return { thesis, tech, thesisHeld };
}

/** Pitch score (spec §5.1): (thesis + post-thesis tech) × demo × team × intro × macro. */
export function pitchScore(ctx: TickContext): number {
  const g = ctx.s.garage!;
  const cfg = ctx.content.economy.garage;
  const m = ctx.s.monthIndex;

  const parts = pitchAppealParts(ctx);
  const appeal = parts.thesis + (parts.thesisHeld ? parts.tech : 0);
  const demoFactor = 1 + cfg.pitch.demoMult * (g.demoStage / cfg.demoStages);
  const hires = DISCIPLINES.reduce((a, d) => a + g.committedHires[d], 0);
  const teamFactor = 1 + cfg.pitch.teamPerHireMult * hires;
  const introFactor = g.warmIntro > g.introSpent ? 1 + cfg.pitch.introMult * g.warmIntro : cfg.pitch.coldCallFactor;
  const sentiment = interpolate(ctx.content.economy.sentiment, m);
  const sting = Math.pow(
    cfg.pitch.failStingFactor,
    g.lastPitchMonth !== null && m - g.lastPitchMonth <= cfg.pitch.failStingMonths ? g.pitchesFailed : 0,
  );
  return appeal * demoFactor * teamFactor * introFactor * (0.6 + 0.8 * sentiment) * sting;
}

/** schedulePitch (command layer calls this after validation): one month of
 *  founder attention, one warm-intro charge, and a term sheet — or a bruise. */
export function runPitch(ctx: TickContext): void {
  const g = ctx.s.garage!;
  const cfg = ctx.content.economy.garage.pitch;
  const m = ctx.s.monthIndex;

  const usedIntro = g.warmIntro > g.introSpent;
  if (usedIntro) g.introSpent += 1;
  // the Valley's memory fades: once the sting window lapses, you're a fresh face
  if (g.lastPitchMonth !== null && m - g.lastPitchMonth > ctx.content.economy.garage.pitch.failStingMonths) {
    g.pitchesFailed = 0;
  }
  const score = pitchScore(ctx);
  g.lastPitchMonth = m;

  if (score < cfg.scoreFloor) {
    g.pitchesFailed += 1;
    emit(ctx, "pitchResult", `The pitch didn't land — partners passed${usedIntro ? " (the intro got you the meeting, not the check)" : ""}. The Valley talks.`, {
      score: Math.round(score * 100) / 100,
      sheet: false,
    });
    return;
  }

  const sigma = cfg.valuationSigma;
  const noise = ctx.rng.logNormal(-(sigma * sigma) / 2, sigma);
  const eraScale = interpolate(ctx.content.economy.eraCapitalScale, m);
  const amount = Math.round(clamp(interpolate(cfg.baseAmount, m) * score * eraScale * noise, cfg.amountMin, cfg.amountMax));
  const preMoney = Math.round(interpolate(cfg.basePreMoney, m) * Math.pow(score, 0.8) * eraScale * noise);
  const sheets = ctx.content.economy.termSheets;
  const sheet: TermSheet = {
    round: 1,
    amount,
    preMoney,
    poolTopUpPct: sheets.poolTopUpEarly,
    boardSeat: amount >= sheets.boardSeatMinAmount,
    downRound: false,
    monthsLeft: sheets.lifeMonths,
  };
  ctx.s.finance.openTermSheet = sheet;
  emit(
    ctx,
    "pitchResult",
    `The pitch landed: a seed sheet — $${(amount / 1e6).toFixed(1)}M at $${(preMoney / 1e6).toFixed(1)}M pre` +
      `${sheet.boardSeat ? ", board seat" : ""}. Accepting it founds the company.`,
    { score: Math.round(score * 100) / 100, sheet: true, amount, preMoney },
  );
}

/** The founding transaction (spec §5.2): accepting a seed sheet in the garage.
 *  Cap-table math is closeRound verbatim on the founder-shares base; founders
 *  and committed recruits land as productive employees; stage → angel. */
export function incorporate(ctx: TickContext, sheet: TermSheet): void {
  const { s } = ctx;
  const g = s.garage!;
  const sc = ctx.content.scenario;
  const cfg = ctx.content.economy.garage.pitch;

  closeRound(ctx, sheet); // financing inflow, dilution, board seat, round history
  charge(ctx, "incorporation", -cfg.incorporationCost, "incorporation, counsel, and first-month scramble");
  // the seed is the STARTING gun, not a round on the normal cadence — the
  // Series A conversation starts immediately (NVIDIA's did)
  s.finance.raiseCooldownMonths = Math.min(s.finance.raiseCooldownMonths, 3);
  // seed pricing values a STORY; venture pricing values milestones. Comparing
  // them flags every honest Series A as a "down round" — don't anchor on it.
  s.finance.lastPostMoney = 0;

  // the founders join their own company (scenario.founders is the discipline
  // split), plus everyone who committed to join "on funding" — no hire ramp
  for (const d of DISCIPLINES) {
    s.people.pools[d].productive += sc.founders[d] + g.committedHires[d];
  }
  s.stage = "angel";
  s.garageLegacy = { discovered: [...g.discovered], demoStage: g.demoStage };
  s.garage = null;
  emit(
    ctx,
    "incorporated",
    `Incorporated. ${totalHeadcount(ctx)} people, $${(s.cash / 1e6).toFixed(1)}M in the bank, ` +
      `${(s.finance.founderShare * 100).toFixed(0)}% founder-owned. The clock is real now.`,
    { headcount: totalHeadcount(ctx), cash: s.cash, founderShare: s.finance.founderShare },
  );

  // strategic development contract (spec §5.4): the thesis-keyed principal
  // signs alongside the seed — P0-lite auto-signs (the accept/decline choice
  // is P1 ask UI); which principal comes calling depends on the founding bet
  const dc = sc.devContracts?.find((d) => d.signByMonth >= s.monthIndex && d.thesisId in s.tree.researched);
  if (dc && !s.devContract) {
    s.devContract = { defId: dc.id, signedMonth: s.monthIndex, cancelled: false };
    emit(
      ctx,
      "devContractSigned",
      `${dc.principal} signs a development contract: $${(dc.nreMonthly / 1e3).toFixed(0)}K/month NRE now, a committed socket later — in exchange for their feature list on your first chip.`,
      { contractId: dc.id, nreMonthly: dc.nreMonthly, royaltyMonthly: dc.royaltyMonthly },
    );
  }
}

/** Monthly garage resolution: burn savings, apply the allocation strip. */
export function processGarage(ctx: TickContext): void {
  const { s } = ctx;
  const g = s.garage!;
  const cfg = ctx.content.economy.garage;
  const gdef = ctx.content.scenario.garage!;
  const m = s.monthIndex;

  // an open seed sheet ages toward expiry exactly like a company-stage sheet
  const sheet = s.finance.openTermSheet;
  if (sheet) {
    sheet.monthsLeft -= ctx.monthsPerTick;
    if (sheet.monthsLeft <= 0) {
      s.finance.openTermSheet = null;
      g.pitchesFailed += 1; // dithering reads like weakness
      emit(ctx, "termSheetExpired", "The seed sheet expired unanswered — the partner moved on.", {});
    }
  }

  const available = garageFounderMonths(ctx);
  const requested = GARAGE_ACTIVITIES.reduce((a, k) => a + (g.alloc[k] ?? 0), 0);
  const scale = requested > available ? available / requested : 1;
  const fm = (k: GarageActivity) => (g.alloc[k] ?? 0) * scale;

  const hasDealmaker = g.founders.some((f) => f.archetype === "dealmaker");
  const hasOperator = g.founders.some((f) => f.archetype === "operator");

  // research: single focus node (P0), founder-months are EM 1:1
  const tree = activeTree(ctx);
  if (tree && g.researchNodeId && fm("research") > 0 && !isResearched(ctx, g.researchNodeId)) {
    const node = getTechNode(tree, g.researchNodeId);
    if (node.garageResearchable && node.availableMonth <= m && prereqsMet(ctx, node)) {
      treeAccrue(ctx, node, fm("research"));
      if (isResearched(ctx, g.researchNodeId)) g.researchNodeId = null;
    }
  }

  // demo: thesis-specific wow factor for the pitch (needs research to show)
  if (fm("demo") > 0 && Object.keys(s.tree.researched).length + Object.keys(s.tree.inProgress).length > 0) {
    g.demoEm += fm("demo");
    g.demoStage = Math.min(cfg.demoStages, Math.floor(g.demoEm / cfg.demoEmPerStage));
  }

  // customer discovery: segments studied in scenario market order
  if (fm("discovery") > 0) {
    g.discoveryEm += fm("discovery");
    const done = Math.floor(g.discoveryEm / cfg.discoveryEmPerSegment);
    const marketIds = ctx.content.markets.map((mk) => mk.id);
    while (g.discovered.length < Math.min(done, marketIds.length)) {
      const next = marketIds.find((id) => !g.discovered.includes(id))!;
      g.discovered.push(next);
      emit(ctx, "garageBeat", `Customer discovery: you understand the ${next} buyers now.`, { marketId: next });
    }
  }

  // consulting: founder time traded for runway
  if (fm("consulting") > 0) {
    const income = Math.round(fm("consulting") * interpolate(cfg.consultingRatePerFm, m));
    g.savings += income;
  }

  // networking: warm intros, dealmaker-boosted
  if (fm("networking") > 0) {
    g.networkingEm += fm("networking") * (hasDealmaker ? cfg.dealmakerNetworkingMult : 1);
    g.warmIntro = Math.min(cfg.warmIntroMax, Math.floor(g.networkingEm / cfg.networkingEmPerLevel));
  }

  // recruiting: commitments that convert on funding day, operator-boosted
  if (fm("recruiting") > 0) {
    g.recruitEm += fm("recruiting") * (hasOperator ? cfg.operatorRecruitingMult : 1);
    const target = Math.min(cfg.recruitMax, Math.floor(g.recruitEm / cfg.recruitEmPerHire));
    let committed = DISCIPLINES.reduce((a, d) => a + g.committedHires[d], 0);
    const cycle: ("design" | "verification" | "implementation")[] = ["design", "implementation", "design", "verification"];
    while (committed < target) {
      g.committedHires[cycle[committed % cycle.length]!] += 1;
      committed += 1;
    }
  }

  // the ramen clock
  g.savings = Math.max(0, g.savings - g.founders.length * gdef.personalBurnMonthly * ctx.monthsPerTick);
}

/** Light close for garage months: no company systems, just the calendar. */
export function closeGarageTick(ctx: TickContext): void {
  const { s } = ctx;
  processHistory(ctx); // the world keeps happening around the booth

  s.finance.ttmRevenue.push(0);
  while (s.finance.ttmRevenue.length > 12) s.finance.ttmRevenue.shift();
  s.kpiHistory.push({
    month: s.monthIndex,
    cash: s.cash,
    revenue: 0,
    nreRevenue: 0,
    cogs: 0,
    opex: 0,
    headcount: 0,
    share: 0,
    runwayMonths: 999,
    activeProjects: 0,
    shippedProducts: 0,
    shareByMarket: {},
    headcountByDiscipline: { design: 0, verification: 0, implementation: 0 },
  });
  s.tick += 1;
  s.monthIndex += ctx.monthsPerTick;
}
